/**
 * What a Print Tags run will actually print (brief §21, §25).
 *
 * Pure, and separate from the modal, because this is the part that must be
 * right: an operator pressing Print is committing a roll of thermal tags and
 * their own time at the sorting table. The modal renders whatever this returns -
 * it does not decide anything itself.
 *
 * The four scopes are the brief's, and each answers a real question:
 *   selected  - "these rooms" (the checkboxes in the grid)
 *   completed - "everything billed this cycle", so nobody ticks 200 boxes
 *   new       - "what I have billed since the last print run"
 *   reprint   - "the ones that already printed", for a tag that fell off
 *
 * `new` and `reprint` narrow to the matching SERVICES inside those rooms, not
 * just the rooms. A room whose Wash & Iron printed this morning and whose Dry
 * Cleaning was added since is "new" - but reprinting the W&I tags alongside it
 * would put two identical WI-1/20 tags on the table, which is exactly what §28
 * exists to prevent.
 */
import { buildRoomTags, orderPieceTotal, tagBlocker } from './tags'
import type { PieceTag, TagBlocker } from './tags'
import type { RoomDocMeta, TagJobRoom } from './b2bRoomDocs'
import type { B2bOrderRecord, B2bRoomRecord } from '../../../services/b2bApi'

export type PrintScope = 'selected' | 'completed' | 'new' | 'reprint'

export const PRINT_SCOPES: Array<{ value: PrintScope; label: string }> = [
  { value: 'selected', label: 'Selected rooms' },
  { value: 'completed', label: 'All completed rooms' },
  { value: 'new', label: 'Rooms with new billing' },
  { value: 'reprint', label: 'Reprint previously printed tags' },
]

export interface PrintScopeInput {
  rooms: B2bRoomRecord[]
  orders: B2bOrderRecord[]
  selectedRoomIds: ReadonlySet<string>
  /** Service slugs the operator has ticked. */
  services: ReadonlySet<string>
  scope: PrintScope
  clientName: string
  copies: number
}

/** One room in the plan, with everything the print job and the UI need. */
export interface TagPlanRoom extends TagJobRoom {
  roomId: string
  roomNumber: string
  /** The orders that will be stamped printed once the job goes out. */
  orders: B2bOrderRecord[]
  /** Already-printed services included in this run - the §28 trigger. */
  reprintOrderIds: string[]
}

export interface TagPlan {
  rooms: TagPlanRoom[]
  /** Tags on the roll, copies included. */
  tagCount: number
  /** Distinct tags, ignoring copies - what the preview shows. */
  pieceCount: number
  roomCount: number
  /** Services skipped, and why, so the modal can say it out loud. */
  blocked: Array<{ roomNumber: string; blocker: TagBlocker }>
  /** Every order this run will stamp. */
  orderIds: string[]
  /** Orders that already carry a printed_at. Non-empty ⇒ confirm before printing. */
  reprintOrderIds: string[]
}

const ordersByRoom = (orders: B2bOrderRecord[]): Map<string, B2bOrderRecord[]> => {
  const map = new Map<string, B2bOrderRecord[]>()
  for (const order of orders) {
    const bucket = map.get(order.roomId)
    if (bucket) bucket.push(order)
    else map.set(order.roomId, [order])
  }
  return map
}

/**
 * The date printed on the tag: when this room's laundry was logged.
 *
 * The most recent order wins. A room billed for Wash & Iron this morning and
 * Dry Cleaning this afternoon is one visit to the sorting table, and the tag
 * that reaches it should carry the day the operator is standing in.
 */
const roomDate = (orders: B2bOrderRecord[]): string =>
  orders.reduce<string>((latest, order) => (order.createdAt > latest ? order.createdAt : latest),
    orders[0]?.createdAt ?? new Date().toISOString())

/** Does this room qualify for the scope, before services are considered? */
const roomInScope = (
  scope: PrintScope,
  roomId: string,
  roomOrders: B2bOrderRecord[],
  selectedRoomIds: ReadonlySet<string>,
): boolean => {
  if (roomOrders.length === 0) return false
  switch (scope) {
    case 'selected':
      return selectedRoomIds.has(roomId)
    case 'completed':
      return true
    case 'new':
      return roomOrders.some((order) => !order.printedAt)
    case 'reprint':
      return roomOrders.some((order) => Boolean(order.printedAt))
    default:
      return false
  }
}

/** Within a qualifying room, the services this scope means. */
const ordersInScope = (scope: PrintScope, roomOrders: B2bOrderRecord[]): B2bOrderRecord[] => {
  if (scope === 'new') return roomOrders.filter((order) => !order.printedAt)
  if (scope === 'reprint') return roomOrders.filter((order) => Boolean(order.printedAt))
  return roomOrders
}

export const buildTagPlan = (input: PrintScopeInput): TagPlan => {
  const { rooms, orders, selectedRoomIds, services, scope, clientName } = input
  const copies = Math.max(1, Math.floor(Number(input.copies)) || 1)
  const byRoom = ordersByRoom(orders)

  const planRooms: TagPlanRoom[] = []
  const blocked: TagPlan['blocked'] = []

  for (const room of rooms) {
    const roomOrders = byRoom.get(room.id) ?? []
    if (!roomInScope(scope, room.id, roomOrders, selectedRoomIds)) continue

    const scoped = ordersInScope(scope, roomOrders).filter((order) => services.has(order.serviceSlug))
    if (scoped.length === 0) continue

    const meta: RoomDocMeta = {
      clientName,
      roomNumber: room.room_number,
      block: room.block,
      date: roomDate(scoped),
    }

    const printable: B2bOrderRecord[] = []
    const tags: PieceTag[] = []

    for (const order of scoped) {
      const blocker = tagBlocker(order)
      if (blocker) {
        // A missing piece count does not stop the rest of the room printing -
        // it removes that one service and says which, so the operator can fix
        // the count and print the remainder now.
        blocked.push({ roomNumber: room.room_number, blocker })
        continue
      }
      printable.push(order)
    }

    if (printable.length === 0) continue
    tags.push(...buildRoomTags(printable))

    planRooms.push({
      roomId: room.id,
      roomNumber: room.room_number,
      meta,
      orders: printable,
      tags,
      reprintOrderIds: printable.filter((order) => order.printedAt).map((order) => order.id),
    })
  }

  const pieceCount = planRooms.reduce((sum, room) => sum + room.tags.length, 0)

  return {
    rooms: planRooms,
    pieceCount,
    tagCount: pieceCount * copies,
    roomCount: planRooms.length,
    blocked,
    orderIds: planRooms.flatMap((room) => room.orders.map((order) => order.id)),
    reprintOrderIds: planRooms.flatMap((room) => room.reprintOrderIds),
  }
}

/**
 * How many rooms each scope would reach, for the radio labels.
 *
 * Counted over the SERVICES the operator has ticked, so unticking Dry Cleaning
 * updates the counts rather than leaving a scope advertising rooms it would now
 * skip. Blocked services are excluded too - a count that includes a room with
 * no piece count promises tags that will not come out.
 */
export const scopeRoomCounts = (
  input: Omit<PrintScopeInput, 'scope' | 'copies'>,
): Record<PrintScope, number> => {
  const counts = {} as Record<PrintScope, number>
  for (const { value } of PRINT_SCOPES) {
    counts[value] = buildTagPlan({ ...input, scope: value, copies: 1 }).roomCount
  }
  return counts
}

/** Services present in this cycle at all - the checkbox list (brief §25). */
export const servicesInCycle = (
  orders: B2bOrderRecord[],
  label: (slug: string) => string,
): Array<{ slug: string; label: string; pieceCount: number }> => {
  const map = new Map<string, number>()
  for (const order of orders) {
    map.set(order.serviceSlug, (map.get(order.serviceSlug) ?? 0) + orderPieceTotal(order))
  }
  return [...map.entries()].map(([slug, pieceCount]) => ({ slug, label: label(slug), pieceCount }))
}
